<template>
  <v-dialog
    :value="value"
    :max-width="maxWidth"
    overlay-color="var(--ds-color-reverse)"
    @input="closeDialog"
  >
    <v-card>
      <v-toolbar>
        <v-toolbar-title>
          {{ filterTitleLabel }}
        </v-toolbar-title>
        <v-spacer />
        <v-toolbar-items class="align-items-center">
          <span>
            <StIcon
              class="close-icon"
              icon-name="close"
              @click="closeDialog"
            />
          </span>
        </v-toolbar-items>
      </v-toolbar>

      <v-row
        no-gutters
        class="mt-5 mx-5"
      >
        <GlobalFilterSummary
          v-if="showGlobalFilterFeature"
          :global-filter-by-data-type="globalFilterByDataTypeToShow"
          :data-type="card.dataType"
        />
      </v-row>

      <v-row
        no-gutters
        class="mt-5 mx-5"
      >
        <v-col
          cols="12"
          md="4"
          class="pr-4"
          @scroll.stop
        >
          <PageFilter
            v-model="tempFilter['pages']"
            :pages-items="pagesFormatted"
            :clearable="displayClearable('pages')"
            @clearInfo="trackEvent"
            @input="changeFilterValue()"
          />
        </v-col>

        <v-col
          v-for="(filter, index) in filtersObj"
          :key="index"
          cols="12"
          md="4"
          class="pr-4"
          @scroll.stop
        >
          <component
            :is="filter.component"
            v-model="tempFilter[filter.filterType]"
            v-bind="filter.props"
            :channels="channels"
            @clearInfo="trackEvent"
          />
        </v-col>
      </v-row>

      <v-row
        no-gutters
        class="pb-5 px-5"
      >
        <v-spacer />
        <v-spacer />
        <v-btn
          v-if="showClearChanges"
          outlined
          rounded
          class="mr-3"
          @click="clearChangesFilter"
        >
          {{ clearChangeLabel }}
        </v-btn>
        <v-btn
          v-if="showClearFilter"
          outlined
          rounded
          class="mr-3"
          @click="clearFilter"
        >
          {{ clearFilterLabel }}
        </v-btn>
        <v-btn
          outlined
          rounded
          @click="applyFilter"
        >
          {{ applyLabel }}
        </v-btn>
      </v-row>
      <ModalAlert
        v-if="showTikTokAlert"
        :alert-message="alertMessage"
        :link-text="linkText"
        :link-reference="linkReference"
        @close-alert="closeAlert()"
      />
    </v-card>
  </v-dialog>
</template>

<script>
import StIcon from '@stilingue/st-icon';
import { DsIcon } from '@ds/icons';
import PageFilter from './PageFilter';
import NegativationFilter from './NegativationFilter';
import SentimentFilter from './SentimentFilter';
import StatusFilter from './StatusFilter';
import SacTypeFilter from './SacTypeFilter';
import UserFilter from './UserFilter';
import DarkPostFilter from './DarkPostFilter';
import CheckedPosts from './CheckedPosts';
import RepliersFilter from './RepliersFilter';
import sacFilterModel from '@/mixins/sac-filter-model';
import filtersModel from '@/mixins/filters-model';
import GlobalFilterSummary from '@/components/GlobalFilterSummary';
import ModalAlert from '@/components/ModalAlert';
import { trackEventWithUserProperties } from '@/utils/mixpanel';
import { isEqual } from 'lodash';

import { Flags } from '@/plugins/rollout';
import { mapGetters } from 'vuex';
import {
  DATA_TYPE,
  FILTER_NAMES,
} from '@/store/modules/cards/constants';
import EventBus from '@/utils/event-bus';

const SHOW_TIKTOK_ALERT = [
  'PropSentimentTimeSeries',
  'PropSentimentGauge',
  'PropBaseShareSentiment',
  'PropSentimentPie',
  'EngagementMatrix',
];

const validateSelectedForTikTok = (fieldContext) => {
  if (Boolean(fieldContext.selectedValues.length)) EventBus.$emit('clearTikTokPages');
};

export default {
  name: 'FilterSacModal',
  components: {
    StIcon,
    DsIcon,
    PageFilter,
    SentimentFilter,
    StatusFilter,
    SacTypeFilter,
    UserFilter,
    DarkPostFilter,
    CheckedPosts,
    RepliersFilter,
    NegativationFilter,
    GlobalFilterSummary,
    ModalAlert,
  },
  mixins: [
    sacFilterModel,
    filtersModel,
  ],
  props: {
    value: {
      type: Boolean,
      default: true,
    },
    card: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      filterLabel: this.$t('components.filter.ModalFilters.label.sac.title'),
      applyLabel: this.$t('components.filter.ModalFilters.label.button.applyLabel'),
      clearFilterLabel: this.$t('components.filter.ModalFilters.label.button.clearFilter'),
      clearChangeLabel: this.$t('components.filter.ModalFilters.label.button.clearchange'),
      appliedFilter: {},
      tempFilter: {},
      channels: [],
      globalFilterByDataType: {},
      globalFilterByDataTypeToShow: {},
      changeOnPageFilter: false,
      tikTokAlertRule: false,
      showTikTokAlert: false,
    };
  },
  computed: {
    ...mapGetters('dashboard', [
      'activeDashboardFilters',
    ]),
    ...mapGetters('featureFlag', [
      'isEnabled',
    ]),
    ...mapGetters('user', [
      'isEditor',
    ]),
    showGlobalFilterFeature() {
      return this.isEnabled(Flags.showGlobalFilter) && this.isEditor;
    },
    showClearChanges() {
      return !(isEqual(this.appliedFilter, this.tempFilter)) || this.changeOnPageFilter;
    },
    filtersObj() {
      const themeItems = [
        {
          text: 'Não Classificados',
          value: 'Sem Temas',
          shouldIgnore: false,
          hasIgnoreOption: true,
        },
        ...(this.activeDashboardFilters?.themes?.map(value => this.generateObjectToNegativationFilter(value)) || []),
      ];

      const tagItems = [
        {
          text: 'Não Classificados',
          value: 'Sem Tags',
          shouldIgnore: false,
          hasIgnoreOption: true,
        },
        ...(this.activeDashboardFilters?.tags?.map(value => this.generateObjectToNegativationFilter(value)) || []),
      ];

      return [
        {
          component: 'SacTypeFilter',
          filterType: FILTER_NAMES.SAC_TYPE,
          props: {
            clearable: this.displayClearable(FILTER_NAMES.SAC_TYPE),
          },
        },
        {
          component: 'StatusFilter',
          filterType: FILTER_NAMES.SAC_STATUS,
          props: {
            clearable: this.displayClearable(FILTER_NAMES.SAC_STATUS),
          },
        },
        {
          component: 'NegativationFilter',
          filterType: FILTER_NAMES.THEME,
          props: {
            label: 'Temas',
            selectItems: themeItems,
            searchable: true,
            clearable: this.displayClearable(FILTER_NAMES.TAG),
            validate() {
              validateSelectedForTikTok(this);
            },
          },
        },
        {
          component: 'NegativationFilter',
          filterType: FILTER_NAMES.TAG,
          props: {
            label: 'Tags',
            selectItems: tagItems,
            searchable: true,
            clearable: this.displayClearable(FILTER_NAMES.TAG),
            validate() {
              validateSelectedForTikTok(this);
            },
          },
        },
        {
          component: 'SentimentFilter',
          filterType: FILTER_NAMES.POLARITY,
          props: {
            clearable: this.displayClearable(FILTER_NAMES.POLARITY),
          },
        },
        {
          component: 'UserFilter',
          filterType: FILTER_NAMES.UIDS,
          props: {
            selectedItems: this.activeDashboardFilters?.uids || {},
            itemId: this.card.id,
            clearable: this.displayClearable(FILTER_NAMES.UIDS),
          },
        },
        {
          component: 'DarkPostFilter',
          filterType: FILTER_NAMES.DARK_POST,
          props: {
            clearable: this.displayClearable(FILTER_NAMES.DARK_POST),
          },
        },
        {
          component: 'CheckedPosts',
          filterType: FILTER_NAMES.FAVORITE,
        },
        {
          component: 'RepliersFilter',
          filterType: FILTER_NAMES.SAC_REPLIER,
          props: {
            label: 'Respondentes',
            selectItems: this.repliersToSelectItem(this.activeDashboardFilters?.repliers),
            clearable: this.displayClearable(FILTER_NAMES.SAC_REPLIER),
          },
        },
      ];
    },
  },
  watch: {
    'tempFilter.pages': {
      handler(newPages, oldPages) {
        if (!isEqual(oldPages, newPages) && Boolean(newPages?.value?.length)) {
          this.channels = Array.from(new Set(this.pagesFormatted
            .filter(({ value }) => newPages?.value.includes(value))
            .map(({ channel }) => channel)));
          this.tempFilter.channels = { value: this.channels };
        }

        if (!isEqual(oldPages, newPages) && !Boolean(newPages?.value?.length)) {
          delete this.tempFilter.channels;
          delete this.tempFilter.pages;
        }
      },
      deep: true,
    },
  },
  created() {
    this.showTikTokAlert = SHOW_TIKTOK_ALERT.some(tikTokComponentName => tikTokComponentName === this.card.component);

    EventBus.$on('changedPages', () => {
      if (this.tempFilter?.pages?.value?.some(page => this.tikTokPages.includes(page)) &&
        this.isSentimentThemeTagSelected(this.tempFilter)) EventBus.$emit('clearTikTokPages');
    });

    EventBus.$on('showTikTokAlert', () => {
      this.showTikTokAlert = true;
    });
  },
  methods: {
    trackEvent(label) {
      trackEventWithUserProperties('ST Card Filter Delete', { Field: label, 'Data Type': DATA_TYPE.SAC });
    },
    changeFilterValue() {
      this.changeOnPageFilter = true;
    },
    closeAlert() {
      this.showTikTokAlert = !this.showTikTokAlert;
    },
  },
};
</script>

<style scoped lang="scss">
  .align-items-center {
    align-items: center;
  }
</style>
